import { Icon, Button, Intent, NonIdealState } from "@blueprintjs/core";
import { Tooltip2 } from "@blueprintjs/popover2";
import { IconNames } from "@blueprintjs/icons";
import { FormikProps } from "formik";
import {
  ButtonContainer,
  ButtonsBloc,
  FGCustomPanel,
  FGListen,
  FGTextAreaInput,
  FieldGroup,
  InlineButtonContainer,
  showError,
  showSuccess,
} from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import * as Yup from "yup";

import {
  DogApi,
  EDogType,
  EPlatform,
  ERequestState,
  IImportRequestDto,
  ImportRequestApi,
  ImportRequestDto,
  ImportRequestTransientOwnersApi,
  ImportRequestTransientOwnersSearch,
  PersonApi,
  RequestApi,
  RequestDto,
} from "../../../../api";
import {
  CancelButton,
  FGDogSearchInput,
  FGPeopleSearchInput,
  FGSRSHDateMaskInput,
  InformationTooltip,
  SharepointButton,
  SmallFormGenerator,
} from "../../../../components";
import { useAuth, useDialog, useEventsContext } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useCrudApi, useTl, useWindow } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { RequestTransientPersonSelection } from "../../request/item/RequestTransientPersonSelection";

export interface IImportRequestDetailProps {
  importRequestId: number;
  editMode: boolean;
}

export const ImportRequestDetail: React.FunctionComponent<IImportRequestDetailProps> = ({
  importRequestId,
  editMode,
}) => {
  const { t } = useTl();
  const transientApi = useApiService(ImportRequestTransientOwnersApi);
  const history = useHistory();
  const api = useApiService(ImportRequestApi);
  const dogApi = useApiService(DogApi);
  const personApi = useApiService(PersonApi);
  const { openNewTab } = useWindow();
  const { showDialogPromise } = useDialog();

  const { user } = useAuth();

  const [ownerIdsBeforeEdition, setOwnerIdsBeforeEdition] = React.useState([]);
  const [dogIdValue, setDogIdValue] = React.useState<number>();

  const { data, loading, saving, saveItem, refresh } = useCrudApi<ImportRequestDto>(
    React.useMemo(
      () => ({
        getApiFn: (id = importRequestId) =>
          id > 0
            ? api.get(id)
            : new ImportRequestDto({
                request: new RequestDto({
                  requestDate: new Date(),
                  status: ERequestState.REQUESTED,
                }),
              }).toJSON(),
        saveApiFn: async (d: ImportRequestDto) => {
          const id = api.save(d);
          const updatedOwnerIds = [...ownerIdsBeforeEdition];
          d.transientOwnerIds?.forEach((ownerId) => {
            if (!updatedOwnerIds.includes(ownerId)) {
              updatedOwnerIds.push(ownerId);
            }
          });
          setOwnerIdsBeforeEdition(updatedOwnerIds);
          return id;
        },
        onSavedRoute: (d) => `${ERoutes.importRequest}/${d.importRequestId}/detail/edit`,
      }),
      [api, importRequestId, ownerIdsBeforeEdition]
    ),
    [importRequestId]
  );

  const onRequestRefreshEvent = React.useCallback(() => {
    refresh();
  }, [refresh]);

  const { subscribeToEvent, unsubscribeEvent } = useEventsContext();
  React.useEffect(() => {
    subscribeToEvent("RequestRefresh", onRequestRefreshEvent);
    return () => unsubscribeEvent("RequestRefresh", onRequestRefreshEvent);
  }, [onRequestRefreshEvent, subscribeToEvent, unsubscribeEvent]);

  const statusEditable = React.useMemo(() => {
    if (data) {
      if (data.request.status) {
        return data.request.status === ERequestState.REQUESTED;
      } else {
        return true;
      }
    }
    return false;
  }, [data]);

  const [saveChecking, setSaveChecking] = React.useState(false);
  const saveCheck = React.useCallback(
    async (values) => {
      setSaveChecking(true);
      let isBanned = false;
      if (importRequestId <= 0) {
        isBanned = (await personApi.isPersonExcludedQuery(values?.request?.personId)).value;
      }
      if (isBanned) {
        const isbannedValidation = await showDialogPromise({
          message: t(ETLCodes.PersonIsBannedConfirmMessage),
          title: t(ETLCodes.Warning),
        });
        if (isbannedValidation !== "yes") return;
      }
      setSaveChecking(false);
      await saveItem(values);
    },
    [importRequestId, personApi, saveItem, showDialogPromise, t]
  );

  const RequestedFormSchema = React.useMemo(() => {
    return Yup.object().shape({
      dogId: Yup.number()
        .nullable()
        .required(t(ETLCodes.Required))
        .test("dog-availability", t(ETLCodes.DogHasAnotherRequestError), async function (value) {
          const res = await api.isDogAvailable(value, importRequestId);
          return res.value;
        })
        .test("dog-has-import-pedigree", t(ETLCodes.DogAlreadyHasImportPedigree), async function (value) {
          const res = await api.checkOnDogPedigree(value);
          return !res.value;
        })
        .test("dog-is-import", t(ETLCodes.DogIsNotImport), async function (value) {
          const type = await dogApi.getDogType(value);
          return type === EDogType.ImportDog;
        })
        .test("dog-deceased", t(ETLCodes.DogIsDeceased), async function (value) {
          if (importRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
            const res = await dogApi.isDogDeceased(value);
            return !res.value;
          }
          return true;
        })
        .test("dog-has-belgian-owners", t(ETLCodes.DogAlreadyHasBelgianOwners), async function (value) {
          if (importRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
            const res = await dogApi.checkBelgianOwnership(value);
            return !res.value;
          }
          return true;
        })
        .test("is-not-excluded", t(ETLCodes.DogIsExcluded), async function (value) {
          if (importRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
            const result = await dogApi.isDogExcluded(value);
            return !result.value;
          }
          return true;
        }),
      request: Yup.object().shape({
        personId: Yup.number()
          .nullable()
          .required(t(ETLCodes.Required))
          .test("is-belgian", t(ETLCodes.IsNotBelgian), async function (value) {
            const res = await personApi.isPersonBelgian(value);
            return res.value;
          })
          .test("missing-adress-information", t(ETLCodes.MissingCompleteAdressOrEmail), async function (value) {
            if (importRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              if (value !== 0 && value !== null && value !== undefined) {
                const result = await personApi.missingAddressInformationCheck(value);
                return result.value;
              }
              return true;
            }
            return true;
          })
          .test("temporary-owner", t(ETLCodes.OwnerIsTemporaryError), async function (value) {
            const res = await dogApi.isDogTemporaryOwner(dogIdValue, value);
            return !res.value;
          })
          .test("is-not-deseased", t(ETLCodes.PersonISDeceased), async function (value) {
            if (importRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              const result = await personApi.isDeceased(value);
              return !result.value;
            }
            return true;
          }),
        requestDate: Yup.date().nullable().required(t(ETLCodes.Required)),
      }),
    });
  }, [api, data, dogApi, dogIdValue, importRequestId, personApi, t]);

  const OtherFormSchema = React.useMemo(() => {
    return Yup.object().shape({});
  }, []);

  const [isBreedNotFciRecognized, setisBreedNotFciRecognized] = React.useState<boolean>();

  const onDogIdChange = React.useCallback(
    async (value: number, formik: FormikProps<IImportRequestDto>) => {
      const fromDb = await transientApi.getIds(
        ImportRequestTransientOwnersSearch.fromJS({
          importRequestId: importRequestId,
          dogId: value,
        })
      );
      var res = await dogApi.dogIsFciRecognizedQuery(value);
      setisBreedNotFciRecognized(!res?.value);
      (window as any)._MYFORMIK = formik;
      setDogIdValue(value);
      setOwnerIdsBeforeEdition(fromDb);
      formik.setFieldValue("transientOwnerIds", fromDb);
    },
    [dogApi, importRequestId, transientApi]
  );

  const onCancel = React.useCallback(() => history.push(`${ERoutes.importRequest}`), [history]);

  const requestApi = useApiService(RequestApi);
  const [shpLoading, setShpLoading] = React.useState(false);
  const existsInShp = React.useMemo(() => !!data?.request.sharepointUri, [data?.request]);
  const openOrCreateInShp = React.useCallback(async () => {
    setShpLoading(true);
    if (!!data?.request.sharepointUri) {
      window.open(data?.request.sharepointUri, "_blank");
      setShpLoading(false);
    } else {
      try {
        await requestApi.createInSharepoint(data?.requestId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setShpLoading(false);
      }
    }
  }, [data?.request.sharepointUri, data?.requestId, requestApi, refresh]);

  const [dogShpLoading, setClubShpLoading] = React.useState(false);
  const dogExistsInShp = React.useMemo(() => !!data?.dogSharepointUri, [data?.dogSharepointUri]);
  const openOrCreateDogInShp = React.useCallback(async () => {
    setClubShpLoading(true);
    if (!!data?.dogSharepointUri) {
      window.open(data?.dogSharepointUri, "_blank");
      setClubShpLoading(false);
    } else {
      try {
        await dogApi.createInSharepoint(data?.dogId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setClubShpLoading(false);
      }
    }
  }, [data?.dogSharepointUri, data?.dogId, dogApi, refresh]);

  const [loadingRegen, setloadingRegen] = React.useState(false);

  const regenDocuments = React.useCallback(async () => {
    setloadingRegen(true);
    try {
      await api.regenExecutionDocuments(importRequestId);
      showSuccess(t(ETLCodes.Success));
    } catch {
      showError(t(ETLCodes.Error));
    }
    setloadingRegen(false);
  }, [api, importRequestId, t]);

  return (
    <SmallFormGenerator
      initialValues={data}
      editMode={editMode}
      onSubmit={saveCheck}
      validationSchema={data?.request?.status === ERequestState.REQUESTED ? RequestedFormSchema : OtherFormSchema}
      loading={loading}
      saving={saving || saveChecking}
      onCancel={onCancel}
      hideButtons={data?.request?.status === ERequestState.EXECUTED}
    >
      <FGListen field="dogId" onChanged={onDogIdChange} />
      <FieldGroup
        columns={2}
        fieldsetProps={{
          title: t(ETLCodes.RequestData),
          rightElement: importRequestId > 0 && (
            <ButtonContainer>
              <SharepointButton existsInShp={existsInShp} loading={shpLoading} onClick={openOrCreateInShp} />
              {data?.request?.status === ERequestState.EXECUTED ? (
                <Button
                  text={t(ETLCodes.RegenImportRequestDocuments)}
                  onClick={regenDocuments}
                  icon={IconNames.UPLOAD}
                  intent={Intent.PRIMARY}
                  loading={loadingRegen}
                />
              ) : null}
            </ButtonContainer>
          ),
        }}
      >
        <div>
          <FGSRSHDateMaskInput
            name="request.requestDate"
            label={t(ETLCodes.RequestDate)}
            requiredMark
            readonly={!statusEditable || data?.request?.isBlocked}
          />
          {data?.request?.status === ERequestState.EXECUTED && (
            <FieldGroup columns={2}>
              <FGSRSHDateMaskInput name="request.executionDate" label={t(ETLCodes.ClosingDate)} readonly={true} />
            </FieldGroup>
          )}
          <FieldGroup columns={[11, 1]}>
            <FGDogSearchInput
              name="dogId"
              label={t(ETLCodes.Dog)}
              onItemViewClick={(item) => openNewTab(`${ERoutes.dog}/${item}/detail/view`)}
              readonly={!statusEditable || data?.request?.isBlocked}
              info={
                <SharepointButton existsInShp={dogExistsInShp} loading={dogShpLoading} onClick={openOrCreateDogInShp} />
              }
            />
            {isBreedNotFciRecognized ? (
              <Tooltip2 interactionKind="hover" content={<div> {t(ETLCodes.BreedNotFciRecognized)} </div>}>
                <Icon intent="danger" icon="warning-sign" iconSize={23} style={{ paddingTop: "0.3rem" }} />
              </Tooltip2>
            ) : null}
          </FieldGroup>
          <FGPeopleSearchInput
            name="request.personId"
            label={t(ETLCodes.Contact)}
            onItemViewClick={(item) => openNewTab(`${ERoutes.people}/${item}/detail/view`)}
            readonly={!statusEditable || data?.request?.isBlocked}
            requiredMark
            info={<InformationTooltip tooltipMessage={ETLCodes.MustBeBelgianOwner} />}
            isBelgian={true}
            showWarning={true}
          />
        </div>

        <FGTextAreaInput
          name="request.remark"
          label={t(ETLCodes.Remark)}
          readonly={data?.request?.status === ERequestState.EXECUTED}
          rows={5}
        />
      </FieldGroup>
      {user.platform === EPlatform.PORTAL ? null : (
        <FGCustomPanel>
          {({ formik }) =>
            formik?.values?.dogId ? (
              <>
                <RequestTransientPersonSelection
                  linkedIds={formik?.values?.transientOwnerIds || null}
                  onLinkedIdsChanged={(ids) => formik?.setFieldValue("transientOwnerIds", ids)}
                  editMode={editMode && !data?.request?.isBlocked && statusEditable}
                  loading={loading}
                />
              </>
            ) : (
              <NonIdealState icon="resolve" description={t(ETLCodes.PleaseSelectADogToEditOwners)} />
            )
          }
        </FGCustomPanel>
      )}
      {data?.request?.status === ERequestState.EXECUTED && (
        <InlineButtonContainer>
          <div />
          <ButtonsBloc style={{ float: "right" }}>
            <CancelButton minimal={false} onClick={() => history.push(`${ERoutes.importRequest}`)} />
          </ButtonsBloc>
        </InlineButtonContainer>
      )}
    </SmallFormGenerator>
  );
};
